import React, { createContext, useContext, ReactNode, createElement } from 'react'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { useStore } from '../../../models'
import { events } from '../../../models/analytics/analytics-events'
import { GameModal, GameModalKinds, LayerType } from '../../../models/game/game-modals'
import { useGameBottomModal } from './useGameBottomModals'
import { WinnerCelebration } from '../../../components/ANewDesign/WinnerCelebration'
import { useGameContext } from '../../GameContainer/GameContext'
import { formatCelebrationMessage } from './useCelebration'
import { useGameState } from '../../GameContainer/useGameState'
import { ChatKind } from '../../../models/api-store'

// #region Types and Context
// TODO might add 'both' in future
type CommandsContext = 'convex' | 'legacy'

// Context for managing CommandsContext
const CommandsContextContext = createContext<CommandsContext | undefined>(undefined)

// Provider component
export const CommandsContextProvider: React.FC<{ context: CommandsContext; children: ReactNode }> = ({
  context,
  children
}) => {
  return createElement(CommandsContextContext.Provider, { value: context }, children)
}

// Hook to access the current CommandsContext
export const useCommandsContext = (): CommandsContext => {
  const context = useContext(CommandsContextContext)
  if (context === undefined) {
    throw new Error('useCommandsContext must be used within a CommandsContextProvider')
  }
  return context
}

interface CommandsHooks {
  handleVideo: () => Promise<void>
  onSendCelebration: (emoji: string) => void
  onSendShowCards: (showLeftCard: boolean, showRightCard: boolean) => Promise<void>
  onSendChat: (message: string, chatKind: ChatKind, color: string, isTyping?: boolean) => void // TODO isTyping needs a default == false
}

type CommandName = keyof CommandsHooks

type CommandsProps = {
  isGameOverScreen?: Parameters<typeof WinnerCelebration>[0]['isGameOverScreen']
  //   isGameOverScreen?: boolean
}

const NotImplementedError = (err: unknown) => {
  console.error('Not implemented', err)
  // throw new Error('Not implemented')
}

// Overload signatures for type safety
// Full object usage
export function useCommonCommands(props: CommandsProps, contextOverride?: CommandsContext): CommandsHooks
// Single command usage
export function useCommonCommands<T extends CommandName>(
  commandName: T,
  props: CommandsProps,
  contextOverride?: CommandsContext
): CommandsHooks[T]

// Implementation
export function useCommonCommands<T extends CommandName>(
  propsOrCommandName: CommandsProps | T,
  propsOrContextOverride: CommandsProps | CommandsContext = {},
  contextOverride?: CommandsContext
): CommandsHooks | CommandsHooks[T] {
  const contextFromProvider = useCommandsContext()

  // Determine if this is the single command or full object usage
  const isSingleCommand = typeof propsOrCommandName === 'string'

  let context: CommandsContext
  let props: CommandsProps
  let commandName: T | undefined

  if (isSingleCommand) {
    // Single command usage: useCommonCommands('handleVideo', {}, contextOverride?)
    commandName = propsOrCommandName as T

    // Check if second argument is context or props
    if (typeof propsOrContextOverride === 'string') {
      props = {}
      context = propsOrContextOverride
    } else {
      props = propsOrContextOverride as CommandsProps
      context = contextOverride ?? contextFromProvider
    }
  } else {
    // Full object usage: useCommonCommands({}, contextOverride?)
    props = propsOrCommandName as CommandsProps
    context = (propsOrContextOverride as CommandsContext | undefined) ?? contextFromProvider
  }

  const useCommandsMap: Record<CommandsContext, (props: CommandsProps) => CommandsHooks> = {
    convex: useConvexCommands,
    legacy: useLegacyCommands
  }

  const useCommands = useCommandsMap[context]
  const allCommands = useCommands(props)

  // Return single command or full object
  return commandName ? allCommands[commandName] : allCommands
}
//#endregion Types and Context

//#region Convex commands implementation
const useConvexCommands = ({ isGameOverScreen }: CommandsProps): CommandsHooks => {
  const context = 'convex'

  // Video Call
  const handleVideo = async () => NotImplementedError(`handleVideo in ${context}`)

  // Celebration
  const { gameId, userId } = useGameContext()
  const { myPlayer } = useGameState()
  const {
    analytics: { setEvent }
  } = useStore()
  const sendMessage = useMutation(api.chat.mutations.sendMessage)
  const setCardVisibility = useMutation(api.players.mutations.setCardVisibility)

  const onSendCelebration: CommandsHooks['onSendCelebration'] = async (emoji) => {
    if (!gameId) return

    setEvent(isGameOverScreen ? events.gameOverScreen.game_celebrated : events.gameOverScreen.hand_celebrated, {
      game_id: gameId,
      emoji_type: emoji
    })

    // Send celebration as an engine message with formatted celebration message
    // Note: The message is formatted with formatCelebrationMessage() for transmission.
    // On the receiving end, use parseCelebrationMessage() to extract the emoji,
    // then createRemoteCelebration() to create the display object.
    const result = await sendMessage({
      gameId,
      userId,
      userName: myPlayer?.name || 'Someone', //'System',
      message: formatCelebrationMessage(emoji),
      chatKind: 'engine',
      color: '#FFD700'
    })
    console.log('onSendCelebration', result)

    return
  }

  // Show Cards
  const onSendShowCards: CommandsHooks['onSendShowCards'] = async (showLeftCard, showRightCard) => {
    if (!gameId) return

    try {
      const result = await setCardVisibility({ gameId, userId, showLeftCard, showRightCard })
    } catch (err) {
      console.error('onSendShowCards', err)
    }
  }

  // Send Chat
  const onSendChat: CommandsHooks['onSendChat'] = async (message, chatKind, color, isTyping) => {
    if (!gameId) return

    // TODO send analytics
    // setEvent(isGameOverScreen ? events.gameOverScreen.game_celebrated : events.gameOverScreen.hand_celebrated, {
    //   game_id: gameId,
    //   emoji_type: emoji
    // })

    const result = await sendMessage({
      gameId,
      userId,
      userName: myPlayer?.name || 'Someone', //'System',
      message,
      chatKind,
      color
    })
    console.log('onSendChat', result)

    return
  }

  return {
    handleVideo,
    onSendCelebration,
    onSendShowCards,
    onSendChat
  }
}
// #endregion Convex commands implementation

//#region Legacy commands implementation
const useLegacyCommands = ({ isGameOverScreen }: CommandsProps): CommandsHooks => {
  // Video Call
  const {
    game: { pushGameModals, setIsCallOptionsModalOpen },
    communication: { isLocalUserJoined }
  } = useStore()

  const { handleOpenCallOptions } = useGameBottomModal()

  const startJoinCall: CommandsHooks['handleVideo'] = async () => {
    setIsCallOptionsModalOpen(true)
    if (isLocalUserJoined) {
      handleOpenCallOptions()
    } else {
      pushGameModals(
        GameModal.create({
          kind: GameModalKinds.options,
          props: {},
          layerType: LayerType.alert // noop
        })
      )
    }
  }

  // Celebration
  const {
    game: { gameId },
    communication: { sendCelebration },
    analytics: { setEvent }
  } = useStore()

  const onSendCelebration: CommandsHooks['onSendCelebration'] = async (emoji) => {
    setEvent(isGameOverScreen ? events.gameOverScreen.game_celebrated : events.gameOverScreen.hand_celebrated, {
      game_id: gameId,
      emoji_type: emoji
    })
    await sendCelebration(emoji) // Okay to call directly as mobx action and not await!
  }

  // Show Cards
  const {
    game: { gameId: gameIdShowCards, gameKind },
    communication: { sendShowCards, sendChat },
    analytics: { setEvent: setEventShowCards }
  } = useStore()

  const onSendShowCards: CommandsHooks['onSendShowCards'] = async (showLeftCard, showRightCard) => {
    const show_cards_type = showLeftCard ? (showRightCard ? 'both' : 'left') : showRightCard ? 'right' : 'both'
    setEventShowCards(events.gameOverScreen.show_cards_tapped, {
      game_id: gameIdShowCards,
      show_cards_type,
      game_kind: gameKind
    })
    await sendShowCards(showLeftCard, showRightCard)
  }

  // Send Chat
  const onSendChat: CommandsHooks['onSendChat'] = async (message, chatKind, color, isTyping) => {
    await sendChat(message, chatKind, color, isTyping)
  }

  // Return handlers
  return {
    handleVideo: startJoinCall,
    onSendCelebration: onSendCelebration,
    onSendShowCards: onSendShowCards,
    onSendChat
  }
}
//#endregion Legacy commands implementation
